import { useState, useCallback } from 'react';
import { ref, get, push, set, update, remove } from 'firebase/database';
import { database } from '../../firebase/config';
import { useAppDataContext } from '../../context/AppDataContext';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from '../../i18n/index.jsx';
import Icon from '../common/Icon';

export default function TodayTasks() {
  const { tasks, setTasks, users } = useAppDataContext();
  const { user } = useAuth();
  const { t } = useTranslation();
  const [newTask, setNewTask] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [sendTarget, setSendTarget] = useState('self'); // 'self' | userId

  const today = new Date().toISOString().split('T')[0];

  // Staff users for send target
  const staffUsers = users.filter(u => u.role === 'staff');

  // 今日任务: admin自己的 + 员工发给admin的
  const todayTasks = tasks.filter(tk => {
    if (tk.date && tk.date !== today) return false;
    // Admin自己创建的（无target或target是self/admin）
    if (!tk.target || tk.target === 'self' || tk.target === 'admin') {
      // 但不是admin发给员工的
      if (tk.target === 'admin' || !tk.target || tk.target === 'self') return true;
    }
    return false;
  });

  // 员工发给管理员的消息
  const staffMessages = tasks.filter(tk => {
    if (tk.date && tk.date !== today) return false;
    return tk.target === 'admin' && tk.from && tk.from !== user.id;
  });

  // Admin发给员工的（显示状态）
  const sentToStaff = tasks.filter(tk => {
    if (tk.date && tk.date !== today) return false;
    return tk.from === user.id && tk.target && tk.target !== 'self' && tk.target !== 'admin';
  });

  const allDisplayTasks = [...todayTasks.filter(tk => !tk.from || tk.from === user.id), ...staffMessages];
  const completedCount = allDisplayTasks.filter(tk => tk.done).length;

  // Helper: find Firebase key by task id
  const findTaskKey = useCallback(async (taskId) => {
    const snap = await get(ref(database, 'tasks'));
    if (!snap.exists()) return null;
    const data = snap.val();
    return Object.entries(data).find(([, v]) => v && v.id === taskId)?.[0] || null;
  }, []);

  const addTask = async () => {
    if (!newTask.trim()) return;
    const task = {
      id: Date.now(),
      text: newTask.trim(),
      done: false,
      date: today,
      createdAt: new Date().toISOString(),
      from: user.id,
      fromName: user.name,
    };

    if (sendTarget !== 'self') {
      const targetUser = users.find(u => u.id === sendTarget);
      task.target = sendTarget;
      task.targetName = targetUser?.name || sendTarget;
    } else {
      task.target = 'self';
    }

    try {
      const taskRef = push(ref(database, 'tasks'));
      await set(taskRef, task);
    } catch (err) {
      console.error('Failed to add task:', err);
    }
    setNewTask('');
    setShowAdd(false);
    setSendTarget('self');
  };

  const toggleTask = async (id) => {
    const task = tasks.find(tk => tk.id === id);
    if (!task) return;
    try {
      const key = await findTaskKey(id);
      if (!key) return;
      if (!task.done) {
        await update(ref(database, '/'), {
          [`tasks/${key}/done`]: true,
          [`tasks/${key}/completedAt`]: new Date().toISOString(),
        });
      } else {
        await update(ref(database, `tasks/${key}`), { done: false });
        await remove(ref(database, `tasks/${key}/completedAt`));
      }
    } catch (err) {
      console.error('Failed to toggle task:', err);
    }
  };

  const deleteTask = async (id) => {
    try {
      const key = await findTaskKey(id);
      if (!key) return;
      await remove(ref(database, `tasks/${key}`));
    } catch (err) {
      console.error('Failed to delete task:', err);
    }
  };

  const updateTask = async (id, text) => {
    try {
      const key = await findTaskKey(id);
      if (!key) return;
      await update(ref(database, '/'), { [`tasks/${key}/text`]: text });
    } catch (err) {
      console.error('Failed to update task:', err);
    }
    setEditingId(null);
  };

  // 导出到iCal格式
  const exportToIcal = (taskList) => {
    const formatDate = (d) => d.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
    const now = new Date();
    const endOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);

    let ical = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//EMH Dashboard//Tasks//EN',
      'CALSCALE:GREGORIAN',
      'METHOD:PUBLISH',
    ];

    taskList.forEach(task => {
      ical.push(
        'BEGIN:VTODO',
        'UID:' + task.id + '@emh-dashboard',
        'DTSTAMP:' + formatDate(now),
        'DTSTART:' + formatDate(now),
        'DUE:' + formatDate(endOfDay),
        'SUMMARY:' + task.text.replace(/[\n,;]/g, ' '),
        'STATUS:' + (task.done ? 'COMPLETED' : 'NEEDS-ACTION'),
        'END:VTODO'
      );
    });

    ical.push('END:VCALENDAR');

    const blob = new Blob([ical.join('\r\n')], { type: 'text/calendar;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'emh-tasks-' + today + '.ics';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="bg-t-card rounded-2xl border border-t-border overflow-hidden">
      <div className="px-4 py-3 border-b border-t-border flex items-center justify-between">
        <div className="flex items-center">
          <Icon name="CheckSquare" size={18} className="text-t-accent mr-2" />
          <span className="text-t-primary font-medium text-sm">{t('tasks.title')}</span>
          {allDisplayTasks.length > 0 && (
            <span className="ml-2 text-t-muted text-xs">{completedCount}/{allDisplayTasks.length}</span>
          )}
          {staffMessages.filter(m => !m.done).length > 0 && (
            <span className="ml-2 px-1.5 py-0.5 bg-red-500 text-white text-xs rounded-full">
              {staffMessages.filter(m => !m.done).length}
            </span>
          )}
        </div>
        <div className="flex gap-1">
          {allDisplayTasks.filter(tk => !tk.done).length > 0 && (
            <button onClick={() => exportToIcal(allDisplayTasks.filter(tk => !tk.done))} className="w-8 h-8 rounded-lg bg-t-surface flex items-center justify-center text-t-tertiary hover:text-blue-500 hover:bg-t-input transition" title={t('tasks.exportCalendar')}>
              <Icon name="Calendar" size={16} />
            </button>
          )}
          <button onClick={() => setShowAdd(!showAdd)} className="w-8 h-8 rounded-lg bg-t-surface flex items-center justify-center text-t-tertiary hover:text-t-accent hover:bg-t-input transition">
            <Icon name={showAdd ? "X" : "Plus"} size={18} />
          </button>
        </div>
      </div>

      {/* 添加任务 */}
      {showAdd && (
        <div className="px-4 py-3 border-b border-t-border bg-t-bg/50 space-y-2">
          {/* 发送对象选择 */}
          <div className="flex gap-1 flex-wrap">
            <button
              onClick={() => setSendTarget('self')}
              className={`px-2 py-1 text-xs rounded transition ${sendTarget === 'self' ? 'bg-t-accent text-t-bg font-medium' : 'bg-t-surface text-t-tertiary hover:bg-t-input'}`}
            >
              {t('tasks.sendSelf')}
            </button>
            {staffUsers.map(su => (
              <button
                key={su.id}
                onClick={() => setSendTarget(su.id)}
                className={`px-2 py-1 text-xs rounded transition ${sendTarget === su.id ? 'bg-blue-500 text-white font-medium' : 'bg-t-surface text-t-tertiary hover:bg-t-input'}`}
              >
                → {su.name}
              </button>
            ))}
          </div>

          <div className="flex gap-2">
            <input
              type="text"
              placeholder={sendTarget === 'self' ? t('tasks.addPlaceholder') : t('tasks.sendPlaceholder')}
              value={newTask}
              onChange={e => setNewTask(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && addTask()}
              autoFocus
              className="flex-1 bg-t-surface border border-t-border rounded-lg px-3 py-2 text-t-primary text-sm placeholder-gray-500 focus:outline-none focus:border-t-accent"
            />
            <button onClick={addTask} disabled={!newTask.trim()} className={`px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed ${sendTarget === 'self' ? 'bg-t-accent text-t-bg' : 'bg-blue-500 text-white'}`}>
              {sendTarget === 'self' ? t('tasks.add') : <Icon name="Send" size={16} />}
            </button>
          </div>
          {/* 快捷预设（仅自己的任务） */}
          {sendTarget === 'self' && (
            <div className="flex gap-1 flex-wrap">
              {t('tasks.presets').map(preset => (
                <button key={preset} onClick={() => setNewTask(preset)} className="px-2 py-1 text-xs bg-t-surface text-t-tertiary rounded hover:bg-t-input hover:text-t-primary transition">
                  {preset}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* 任务列表 */}
      <div className="divide-y divide-t-border max-h-72 overflow-y-auto">
        {allDisplayTasks.length === 0 ? (
          <div className="px-4 py-8 text-center text-t-muted text-sm">
            <Icon name="Coffee" size={24} className="mx-auto mb-2 opacity-50" />
            {t('tasks.noTasks')}
          </div>
        ) : (
          allDisplayTasks.sort((a, b) => a.done - b.done).map(task => {
            const isFromStaff = task.from && task.from !== user.id && task.target === 'admin';
            const isSentToStaff = task.from === user.id && task.target && task.target !== 'self' && task.target !== 'admin';
            return (
              <div key={task.id} className={`px-4 py-3 flex items-center gap-3 group hover:bg-t-surface/50 transition ${task.done ? 'opacity-50' : ''}`}>
                <button onClick={() => toggleTask(task.id)} className={`w-5 h-5 rounded-full border-2 flex-shrink-0 flex items-center justify-center transition ${task.done ? 'bg-green-500 border-green-500' : isFromStaff ? 'border-blue-500 hover:border-blue-400' : 'border-gray-500 hover:border-t-accent'}`}>
                  {task.done && <Icon name="Check" size={12} className="text-white" />}
                </button>

                <div className="flex-1 min-w-0">
                  {editingId === task.id ? (
                    <input
                      type="text"
                      defaultValue={task.text}
                      autoFocus
                      onBlur={e => updateTask(task.id, e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && updateTask(task.id, e.target.value)}
                      className="w-full bg-transparent text-t-primary text-sm focus:outline-none"
                    />
                  ) : (
                    <>
                      <span onClick={() => !task.done && !isFromStaff && setEditingId(task.id)} className={`text-sm cursor-pointer ${task.done ? 'text-t-muted line-through' : 'text-t-primary'}`}>
                        {task.text}
                      </span>
                      {isFromStaff && (
                        <div className="text-xs text-blue-400 mt-0.5">
                          ← {task.fromName || t('tasks.fromStaff')}
                        </div>
                      )}
                      {isSentToStaff && (
                        <div className="text-xs text-indigo-400 mt-0.5">
                          → {task.targetName}
                        </div>
                      )}
                    </>
                  )}
                </div>

                <button onClick={() => deleteTask(task.id)} className="opacity-0 group-hover:opacity-100 text-t-muted hover:text-red-500 transition flex-shrink-0">
                  <Icon name="Trash2" size={14} />
                </button>
              </div>
            );
          })
        )}
      </div>

      {/* 已发送给员工的任务（折叠） */}
      {sentToStaff.length > 0 && (
        <div className="px-4 py-2 bg-indigo-500/10 border-t border-t-border">
          <div className="text-indigo-400 text-xs">
            {t('tasks.sentToStaff', { count: sentToStaff.length })} · {sentToStaff.filter(tk => tk.done).length} {t('tasks.done')}
          </div>
        </div>
      )}

      {/* 完成的任务 */}
      {completedCount > 0 && completedCount === allDisplayTasks.length && (
        <div className="px-4 py-2 bg-green-500/10 text-green-500 text-xs text-center">
          {t('tasks.allCompleted')}
        </div>
      )}
    </div>
  );
}
